import React, { Component, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "reactstrap";
import "./SubscriptionForm.css";
import { type InjectedFormProps, reduxForm } from "redux-form";
import { AgeConsentCheckbox } from "./AgeConsentCheckbox";
import type ISubscriptionData from "landing/forms/ISubscriptionData";
import type { LandingPageConfig } from "landing/objects/LandingPageConfig";
import type { OAuthType } from "oauth/actions/OAuthActionCreators";
import DateInput from "shared/forms/inputs/DateInput";
import EmailInput from "shared/forms/inputs/EmailInput";
import PhoneNoInput from "shared/forms/inputs/PhoneNoInput";
import PostalCodeInput from "shared/forms/inputs/PostalCodeInput";
import { StoreInput } from "shared/forms/inputs/StoreInput";
import TextInput from "shared/forms/inputs/TextInput";
import { TermTemplate } from "templates/terms/TermTemplate";

export const FIELD_FIRST_NAME = "firstName";
export const FIELD_LAST_NAME = "lastName";
export const FIELD_EMAIL = "email";
export const FIELD_MOBILE_NUMBER = "mobileNumber";
export const FIELD_POSTAL_CODE = "postalCode";
export const FIELD_STORE_UUID = "storeUUID";
export const FIELD_BIRTHDAY = "birthday";
export const FIELD_TNC = "TNC";

interface ISubscriptionFormWithFormProps
  extends ISubscriptionFormProps,
    InjectedFormProps<ISubscriptionData, ISubscriptionFormProps> {}

interface ISubscriptionFormWithFormState {
  isSocialLogin: boolean;
}

class SubscriptionFormInner extends Component<
  ISubscriptionFormWithFormProps,
  ISubscriptionFormWithFormState
> {
  public state = {
    isSocialLogin: false,
  };

  public componentDidMount(): void {
    const { landingPageConfig } = this.props;
    if (
      landingPageConfig.oaAuthFirstName &&
      landingPageConfig.oaAuthFirstName &&
      landingPageConfig.oaAuthEmail
    ) {
      const { change } = this.props;

      change(FIELD_FIRST_NAME, landingPageConfig.oaAuthFirstName);
      change(FIELD_LAST_NAME, landingPageConfig.oaAuthLastName);
      change(FIELD_EMAIL, landingPageConfig.oaAuthEmail);

      this.setState({
        isSocialLogin: true,
      });
    }
  }

  public onFacebookLogin = (): void => {
    this.props.onGoOAuth("facebook");
  };

  public onGoogleLogin = (): void => {
    this.props.onGoOAuth("google");
  };

  public onRemoveSocial = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ): void => {
    const { change } = this.props;
    change(FIELD_FIRST_NAME, "");
    change(FIELD_LAST_NAME, "");
    change(FIELD_EMAIL, "");

    this.setState({
      isSocialLogin: false,
    });

    e.preventDefault();
  };

  public render(): ReactNode {
    const { landingPageConfig, invalid, submitting, handleSubmit, onSubmit } =
      this.props;
    const { isSocialLogin } = this.state;
    return (
      <form className="subscription-form" onSubmit={handleSubmit(onSubmit)}>
        <h2>{landingPageConfig.lpHeader1}</h2>

        {landingPageConfig.lpHeader2 && <h3>{landingPageConfig.lpHeader2}</h3>}

        {landingPageConfig.lpHeader3 && <h3>{landingPageConfig.lpHeader3}</h3>}

        {landingPageConfig.lpHeader4 && <h3>{landingPageConfig.lpHeader4}</h3>}

        {isSocialLogin ? (
          <FormattedMessage
            id="landing.subscription.social.valid_mobile_number_required"
            values={{
              useAnotherAccount: (
                <a href="" onClick={this.onRemoveSocial}>
                  <FormattedMessage id="landing.subscription.social.use_another_account" />
                </a>
              ),
            }}
          />
        ) : landingPageConfig.isFacebookSignup ||
          landingPageConfig.isGoogleSignup ? (
          <div className="social-buttons">
            {landingPageConfig.isFacebookSignup && (
              <FormattedMessage id="landing.subscription.social.facebook">
                {(text: string) => (
                  <Button
                    onClick={this.onFacebookLogin}
                    className="social-facebook"
                  >
                    <i className="fab fa-facebook" />
                    {text}
                  </Button>
                )}
              </FormattedMessage>
            )}

            {/*<FacebookLoginWithButton*/}
            {/*    appId="1621182618166293"*/}
            {/*    fields="first_name,last_name,email"*/}
            {/*    callback={this.onFacebookResponse}*/}
            {/*    icon="fa-facebook"*/}
            {/*    size="small"*/}
            {/*    textButton={text}*/}
            {/*/>*/}

            {/*<GoogleLoginWithButton*/}
            {/*    clientId="1064978527253-mpgercalvcdcc869gecp79it9k0v840e.apps.googleusercontent.com"*/}
            {/*    buttonText={text}*/}
            {/*    onSuccess={this.onGoogleResponse}*/}
            {/*    onFailure={this.onGoogleFailure}*/}
            {/*    cookiePolicy={'single_host_origin'}*/}

            {/*/>*/}

            {landingPageConfig.isGoogleSignup && (
              <FormattedMessage id="landing.subscription.social.google">
                {(text: string) => (
                  <Button
                    onClick={this.onGoogleLogin}
                    className="social-google"
                  >
                    <i className="fab fa-google" />
                    {text}
                  </Button>
                )}
              </FormattedMessage>
            )}

            <div className="horizontal-rule">
              <hr />
              <span>
                <FormattedMessage id="landing.subscription.or" />
              </span>
            </div>
          </div>
        ) : null}

        <p>
          <em>
            <FormattedMessage id="landing.subscription.all_fields_mandatory" />
          </em>
        </p>

        <div className="subscription-form-fields">
          {!landingPageConfig.hidesFirstName && (
            <TextInput
              inputID={FIELD_FIRST_NAME}
              label={
                <FormattedMessage id="landing.subscription.field.first_name" />
              }
              disabled={isSocialLogin}
            />
          )}

          {!landingPageConfig.hidesLastName && (
            <TextInput
              inputID={FIELD_LAST_NAME}
              label={
                <FormattedMessage id="landing.subscription.field.last_name" />
              }
              disabled={isSocialLogin}
            />
          )}

          {!landingPageConfig.hidesEmail && (
            <EmailInput
              inputID={FIELD_EMAIL}
              label={
                <FormattedMessage id="landing.subscription.field.email_address" />
              }
              disabled={isSocialLogin}
            />
          )}

          <PhoneNoInput
            inputID={FIELD_MOBILE_NUMBER}
            label={
              <FormattedMessage id="landing.subscription.field.mobile_number" />
            }
            mobileLockMobileNumber={landingPageConfig.mobileNumber}
          />

          {landingPageConfig.showsPostalCode && (
            <PostalCodeInput
              inputID={FIELD_POSTAL_CODE}
              label={
                <FormattedMessage id="landing.subscription.field.postal_code" />
              }
            />
          )}

          {landingPageConfig.showsDOB && (
            <DateInput
              inputID={FIELD_BIRTHDAY}
              label={
                <FormattedMessage id="landing.subscription.field.birthday" />
              }
            />
          )}

          {landingPageConfig.showsStoreSelector &&
            landingPageConfig.storeList && (
              <StoreInput
                inputID={FIELD_STORE_UUID}
                label={
                  <FormattedMessage id="landing.subscription.field.birthday" />
                }
              >
                {landingPageConfig.storeList.map(({ storeUUID, storeName }) => (
                  <option key={storeUUID} value={storeUUID}>
                    {storeName}
                  </option>
                ))}
              </StoreInput>
            )}

          <div className="toc">
            <TermTemplate landingPageConfig={landingPageConfig} />

            {landingPageConfig.consentAge && (
              <AgeConsentCheckbox consentAge={landingPageConfig.consentAge} />
            )}
          </div>

          <p>
            <Button color="primary" disabled={invalid || submitting}>
              <FormattedMessage id="landing.subscription.button.sign_up" />
            </Button>
          </p>
        </div>
      </form>
    );
  }
}

export interface ISubscriptionFormProps {
  landingPageConfig: LandingPageConfig;
  onSubmit: (formData: ISubscriptionData) => Promise<void>;
  onGoOAuth: (oauthType: OAuthType) => void;
}

export default reduxForm<ISubscriptionData, ISubscriptionFormProps>({
  form: "SubscriptionForm",
})(SubscriptionFormInner);
